// @ts-nocheck
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setModalOpen } from "../../store/modalSlice";
import { generateClient } from "aws-amplify/api";
import { ReactComponent as LightningIcon } from "../../assets/icons/lightning.svg";
import ModalBase from "./ModalBase";
import { getAssetWithComposedForecasts, listProcessedModelSchemasWithOutputs, createComposedForecastCustom, createComposedForecastDataSourceCustom } from "../../graphql/customQueries";

import Select, { Field } from "../ReusableComponents/Select";
const CustomForecastModal = ({ asset }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const client = generateClient();

  // State management
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [existingForecasts, setExistingForecasts] = useState([]);
  const [availableSchemas, setAvailableSchemas] = useState([]);
  const [selectedOutputParam, setSelectedOutputParam] = useState("");
  const [forecastConfig, setForecastConfig] = useState({
    name: "",
    description: ""
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load existing composed forecasts for this asset
      const assetData = await client.graphql({
        query: getAssetWithComposedForecasts,
        variables: { id: asset.id }
      });
      setExistingForecasts(assetData.data.getGeoPoint.composedForecasts.items);

      // Load available ProcessedModelSchemas with their output parameters
      const schemasData = await client.graphql({
        query: listProcessedModelSchemasWithOutputs,
        variables: {
          filter: {
            isActive: { eq: true }
          }
        }
      });
      setAvailableSchemas(schemasData.data.listProcessedModelSchemas.items);

    } catch (error) {
      console.error("Error loading data:", error);
      setErrors({ load: "Failed to load forecast data" });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    dispatch(setModalOpen(false));
  };

  const handleInputChange = (field, value) => {
    setForecastConfig(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!forecastConfig.name.trim()) newErrors.name = "Name is required";
    if (!selectedOutputParam) newErrors.selectedOutputParam = "Please select an output parameter";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);

      // Find the selected output parameter details
      const selectedParam = findSelectedOutputParam();
      if (!selectedParam) {
        setErrors({ submit: "Selected output parameter not found" });
        return;
      }

      // Step 1: Create ComposedForecast
      const composedForecastInput = {
        name: forecastConfig.name,
        description: forecastConfig.description,
        assetId: asset.id,
        forecastType: "simple",
        aggregationMethod: "direct",
        updateFrequency: "daily",
        forecastHorizon: 7,
        outputParameters: JSON.stringify([selectedParam.name]),
        isActive: true,
        owner: user?.permissionDetails?.id || "",
        organizationId: asset.organizationId || ""
      };

      const forecastResult = await client.graphql({
        query: createComposedForecastCustom,
        variables: { input: composedForecastInput }
      });

      const createdForecast = forecastResult.data.createComposedForecast;

      // Step 2: Create ComposedForecastDataSource
      const dataSourceInput = {
        composedForecastId: createdForecast.id,
        sourceType: "PROCESSED_MODEL_OUTPUT",
        sourceName: `${selectedParam.schema.name} - ${selectedParam.name}`,
        sourceDescription: selectedParam.description,
        processedModelOutputId: selectedParam.id,
        weight: 1.0,
        priority: 0,
        isActive: true,
        timeAlignment: "nearest",
        temporalResolution: "daily",
        owner: user?.permissionDetails?.id || "",
        organizationId: asset.organizationId || ""
      };

      await client.graphql({
        query: createComposedForecastDataSourceCustom,
        variables: { input: dataSourceInput }
      });

      handleClose();

    } catch (error) {
      console.error("Error creating forecast:", error);
      setErrors({ submit: `Failed to create forecast: ${error.message}` });
    } finally {
      setSubmitting(false);
    }
  };

  const findSelectedOutputParam = () => {
    for (const schema of availableSchemas) {
      const param = schema.outputs.items.find(output => output.id === selectedOutputParam);
      if (param) {
        return { ...param, schema };
      }
    }
    return null;
  };

  const renderSchemaOptions = () => {
    return availableSchemas.map(schema => {
      if (schema.outputs.items.length === 0) return null;

      return (
        <optgroup key={schema.id} label={`${schema.name} (${schema.type})`}>
          {schema.outputs.items.map(output => (
            <option key={output.id} value={output.id}>
              {output.name} {output.units ? `(${output.units})` : ''} - {output.description}
            </option>
          ))}
        </optgroup>
      );
    });
  };

  if (loading) {
    return (
      <ModalBase
        title="Add Custom Forecast"
        icon={<LightningIcon className="h-6 w-6 [&_path]:stroke-current text-eai-hero-accent" />}
        onClose={handleClose}
        size="md"
      >
        <div className="eai-description-sm flex items-center justify-center py-16">
          Loading forecast data…
        </div>
      </ModalBase>
    );
  }

  return (
    <ModalBase
      title={`Add Custom Forecast — ${asset.name}`}
      icon={<LightningIcon className="h-6 w-6 [&_path]:stroke-current text-eai-hero-accent" />}
      onClose={handleClose}
      size="md"
      footer={
        <>
          <button type="button" onClick={handleClose} className="eai-btn-outline" disabled={submitting}>
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={submitting} className="eai-btn-cta">
            {submitting ? "Creating…" : "Create"}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        {existingForecasts.length > 0 && (
          <div>
            <h3 className="eai-card-title mb-3">Existing composed dashboards</h3>
            <div className="grid gap-2">
              {existingForecasts.map((forecast) => (
                <div key={forecast.id} className="eai-card flex items-start justify-between gap-3 p-4">
                  <div className="min-w-0">
                    <div className="eai-card-title truncate">{forecast.name}</div>
                    <p className="eai-description-sm">{forecast.description}</p>
                    <div className="eai-meta-label mt-1">Simple forecast from model output data</div>
                  </div>
                  <span
                    className={`eai-tag-pill-sm shrink-0 ${
                      forecast.isActive ? "border-eai-cta-primary bg-eai-cta-primary text-white" : ""
                    }`}
                  >
                    {forecast.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <h3 className="eai-card-title mb-3">Create new composed dashboard</h3>

          <div className="flex flex-col gap-4">
            <Field label="Forecast name *" htmlFor="forecast-name" error={errors.name}>
              <input
                id="forecast-name"
                type="text"
                value={forecastConfig.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                className={`eai-input ${errors.name ? "border-eai-danger-border-hover" : ""}`}
                placeholder="Enter forecast name"
              />
            </Field>

            <Field label="Description" htmlFor="forecast-description">
              <textarea
                id="forecast-description"
                value={forecastConfig.description}
                onChange={(e) => handleInputChange("description", e.target.value)}
                rows={2}
                className="eai-input"
                placeholder="Describe this forecast"
              />
            </Field>

            <Field
              label="Output parameter *"
              htmlFor="forecast-output"
              error={errors.selectedOutputParam}
              hint="Choose which model output parameter to use as the data source for this forecast."
            >
              <Select
                id="forecast-output"
                value={selectedOutputParam}
                onChange={(e) => setSelectedOutputParam(e.target.value)}
                className={errors.selectedOutputParam ? "border-eai-danger focus:border-eai-danger focus:ring-eai-danger" : ""}
              >
                <option value="">Select an output parameter…</option>
                {renderSchemaOptions()}
              </Select>
            </Field>
          </div>
        </div>

        {(errors.submit || errors.load) && (
          <div className="rounded-eai-card border border-eai-danger-border bg-eai-danger-surface p-3">
            <p className="font-inter text-eai-body-sm text-eai-danger-text">{errors.submit || errors.load}</p>
          </div>
        )}
      </div>
    </ModalBase>
  );
};

export default CustomForecastModal;
